// What the site says about support hours, and what "Call me about this" promises when it is
// pressed. The hours themselves live in lib/contact-hours.ts (8 AM - 8 PM Monday to Friday,
// 10 AM - 6 PM Saturday, closed Sunday and public holidays, on the Auckland clock); this file is
// the wording built on them, so every page that names the hours reads the same string.

import { CHAT_HOURS, isWithinChatHours, nzClock, type Weekday } from '~/lib/contact-hours';

/** The hours as the site states them, for any copy that names them. */
export const CHAT_HOURS_LABEL = '8am–8pm Monday to Friday, 10am–6pm Saturday, closed Sundays and public holidays';

const WEEKDAYS: readonly Weekday[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_NAMES: Record<Weekday, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
};

/**
 * The next time chat opens after `date`, in Auckland: how many days ahead (0 = later today), the
 * day, and the hour it opens that day. Null while chat is open.
 */
export function nextChatOpening(date: Date = new Date()): { daysAhead: number; weekday: Weekday; opens: number } | null {
  if (isWithinChatHours(date)) return null;
  const { weekday, minutes } = nzClock(date);
  const today = CHAT_HOURS[weekday];
  if (today && minutes < today[0] * 60) return { daysAhead: 0, weekday, opens: today[0] };
  let index = WEEKDAYS.indexOf(weekday);
  for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
    index = (index + 1) % 7;
    const hours = CHAT_HOURS[WEEKDAYS[index]];
    if (hours) return { daysAhead, weekday: WEEKDAYS[index], opens: hours[0] };
  }
  return null;
}

export interface SupportCallback {
  inHours: boolean;
  message: string;
}

const clock = (hour: number): string => (hour < 12 ? `${hour}AM` : hour === 12 ? '12PM' : `${hour - 12}PM`);

export const supportCallbackMessage = (now: Date = new Date()): SupportCallback => {
  const next = nextChatOpening(now);
  if (!next) return { inHours: true, message: "We'll be on the phone to you shortly." };

  // A callback that lands the same day is promised as "shortly" rather than given a clock time:
  // naming the opening hour to someone who asked before the desk opened reads as a delay, and
  // the customer does not need to be told our hours to know the call is coming.
  if (next.daysAhead === 0) return { inHours: false, message: "Hold tight. We'll be on the phone to you shortly." };

  // "Tomorrow" for the next day, except that a Sunday asker is told "Monday": naming the day reads
  // better than "tomorrow" when the desk has been shut all weekend.
  const when = next.daysAhead === 1 && next.weekday !== 'Mon' ? 'tomorrow' : DAY_NAMES[next.weekday];
  return { inHours: false, message: `Hold tight. We'll be on the phone to you around ${clock(next.opens)} ${when}.` };
};
